import {
  Button,
  formatMoney,
  type MapTruck,
  PageEmpty,
  PageError,
  PageHeader,
  PageLoading,
  ZoneMap,
} from "@majitap/ui";
import { Badge } from "@majitap/ui/arc/badge/badge";
import { LineChart } from "@majitap/ui/arc/line-chart/line-chart";
import { MetricCard } from "@majitap/ui/arc/metric-card/metric-card";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { ArrowRight, Banknote, ChevronRight, Droplet, HandCoins, TriangleAlert, Truck } from "lucide-react";
import type { ReactNode } from "react";
import { errorOf } from "../api";
import { useCompanyFormat } from "../company";
import { type Counts, countsQuery } from "../counts";
import { messageFor } from "../error-copy";
import type { ConsoleItem } from "../nav";
import {
  changeVsBefore,
  fleetNow,
  greeting,
  litres,
  longDay,
  moneyToday,
  needsYou,
  type Overview,
  overviewQuery,
  pipeline,
  registrationChart,
  type Waiting,
} from "../overview";
import { RouterLink } from "../router-link";
import { meQuery } from "../session";
import { truckLine, truckPositionsQuery, truckState } from "../tracking";
import { toMapZone, zonesListQuery } from "../zones";
import styles from "./overview.module.css";

const ICON: Record<Waiting["icon"], typeof Truck> = {
  truck: Truck,
  alert: TriangleAlert,
  payout: HandCoins,
  till: Banknote,
  tank: Droplet,
};

function Pipeline({ dispatch, figures }: { dispatch: NonNullable<Counts["dispatch"]>; figures: Overview }) {
  const { total, stages } = pipeline(dispatch);
  const done = stages.find((s) => s.id === "done")?.value ?? 0;
  const l = figures.deliveries?.litres;
  return (
    <section className={styles.panel} aria-label="Today's deliveries">
      <div className={styles.pipelineTop}>
        <div>
          <p className={styles.muted}>Today's deliveries</p>
          <p className={styles.headline}>
            <span className={styles.big}>{done}</span> of {total} delivered
          </p>
        </div>
        {l?.planned ? (
          <div className={styles.end}>
            <p className={styles.muted}>Litres delivered</p>
            <p className={styles.headline}>
              <span className={styles.mid}>{litres(l.delivered)}</span> of {litres(l.planned)} planned
            </p>
          </div>
        ) : null}
      </div>
      <div className={styles.bar} role="img" aria-label={stages.map((s) => `${s.label} ${s.value}`).join(", ")}>
        {stages.map((s) =>
          s.value ? (
            <span key={s.id} className={styles.segment} data-stage={s.id} style={{ flexGrow: s.value }} />
          ) : null,
        )}
      </div>
      <ul className={styles.stages}>
        {stages.map((s) => (
          <li key={s.id}>
            <RouterLink href={s.href} className={styles.stage} aria-label={`${s.label}: ${s.value}. ${s.hint}`}>
              <span className={styles.stageHead}>
                <span className={styles.dot} data-stage={s.id} aria-hidden />
                {s.label}
              </span>
              <span className={styles.stageValue} data-stage={s.id}>
                {s.value}
              </span>
              <span className={styles.hint}>{s.hint}</span>
            </RouterLink>
          </li>
        ))}
      </ul>
    </section>
  );
}

function NeedsYou({ items }: { items: Waiting[] }) {
  return (
    <section className={`${styles.panel} ${styles.flush}`} aria-label="Needs you">
      <div className={styles.panelHead}>
        <h2 className={styles.title}>Needs you</h2>
        {items.length ? <Badge size="sm">{`${items.length} waiting`}</Badge> : null}
      </div>
      {items.length === 0 ? (
        <p className={styles.nothing}>Nothing is waiting for you.</p>
      ) : (
        <ul className={styles.rows}>
          {items.map((w) => {
            const Icon = ICON[w.icon];
            return (
              <li key={w.id} className={styles.row}>
                <span className={styles.icon} data-tone={w.tone} aria-hidden>
                  <Icon size={18} />
                </span>
                <span className={styles.rowText}>
                  <span className={styles.rowTitle}>{w.title}</span>
                  {w.detail ? <span className={styles.hint}>{w.detail}</span> : null}
                </span>
                <RouterLink href={w.href} className={styles.action} aria-label={`${w.action}: ${w.title}`}>
                  {w.action} <ChevronRight size={14} aria-hidden />
                </RouterLink>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function MoneyToday({ figures }: { figures: Overview }) {
  const format = useCompanyFormat();
  const items = moneyToday(figures);
  if (!items.length) return null;
  return (
    <section className={styles.panel} aria-label="Money today">
      <div className={styles.panelHeadInline}>
        <h2 className={styles.title}>Money today</h2>
      </div>
      <ul className={styles.figures}>
        {items.map((m) => (
          <li key={m.id}>
            <RouterLink href={m.href} className={styles.figure}>
              <span className={styles.muted}>{m.label}</span>
              <span className={styles.figureValue} data-tone={m.tone}>
                {formatMoney(m.amount, format)}
              </span>
              <span className={styles.hint}>{m.hint}</span>
            </RouterLink>
          </li>
        ))}
      </ul>
    </section>
  );
}

function FleetNow() {
  const positions = useQuery(truckPositionsQuery);
  const zones = useQuery(zonesListQuery);
  const list = positions.data?.data ?? [];
  const fleet = fleetNow(list);
  const trucks: MapTruck[] = list
    .filter((p) => p.on_shift)
    .map((p) => ({
      id: p.truck_id,
      lng: p.lng,
      lat: p.lat,
      state: truckState(p),
      label: p.plate_number,
      title: `${p.plate_number}, ${truckLine(p)}`,
    }));
  return (
    <section className={`${styles.panel} ${styles.fleet}`} aria-label="Fleet now">
      <div className={styles.panelHeadInline}>
        <h2 className={styles.title}>Fleet now</h2>
        <Badge tone="success" size="sm" icon={<span className={styles.liveDot} aria-hidden />}>
          Live
        </Badge>
      </div>
      <div className={styles.map}>
        <ZoneMap
          label="Trucks on shift"
          height="100%"
          zones={(zones.data ?? []).map(toMapZone)}
          trucks={trucks}
          fitAll
        />
      </div>
      <dl className={styles.stats}>
        <div>
          <dt>On shift</dt>
          <dd>{fleet.onShift}</dd>
        </div>
        <div>
          <dt>Free now</dt>
          <dd data-tone={fleet.free ? "success" : undefined}>{fleet.free}</dd>
        </div>
        <div>
          <dt>On a job</dt>
          <dd>{fleet.onJob}</dd>
        </div>
        <div>
          <dt>Not seen for 5+ min</dt>
          <dd data-tone={fleet.notSeen ? "warning" : undefined}>
            {fleet.notSeen ? `${fleet.notSeen} · ${fleet.notSeenPlates.slice(0, 2).join(", ")}` : 0}
          </dd>
        </div>
      </dl>
      <RouterLink href="/fleet/map" className={styles.link}>
        Open live map <ArrowRight size={14} aria-hidden />
      </RouterLink>
    </section>
  );
}

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("");

/** "10:18" today, else "21 Oct". */
function shortWhen(iso: string, timezone: string) {
  const day = (d: Date) => new Intl.DateTimeFormat("en-CA", { timeZone: timezone }).format(d);
  const at = new Date(iso);
  return day(at) === day(new Date())
    ? new Intl.DateTimeFormat("en-GB", { timeZone: timezone, hour: "2-digit", minute: "2-digit" }).format(at)
    : new Intl.DateTimeFormat("en-GB", { timeZone: timezone, day: "numeric", month: "short" }).format(at);
}

/** "Fri 17 Oct", read in the chart's tooltip and data table. */
const chartDay = (date: string) =>
  new Intl.DateTimeFormat("en-GB", { timeZone: "UTC", weekday: "short", day: "numeric", month: "short" }).format(
    new Date(`${date}T12:00:00Z`),
  );

function Registrations({ r }: { r: NonNullable<Overview["registrations"]> }) {
  const format = useCompanyFormat();
  const chart = registrationChart(r.days);
  const recent = r.last_7_days.people + r.last_7_days.businesses;
  const before = r.previous_7_days.people + r.previous_7_days.businesses;
  const change = changeVsBefore(recent, before);
  return (
    <section className={styles.registrations} aria-labelledby="registrations-title">
      <div className={styles.panelHeadInline}>
        <h2 id="registrations-title" className={styles.title}>
          Customer registrations
        </h2>
        <span className={styles.hint}>Last 7 days vs the 7 before</span>
      </div>
      <div className={styles.metrics}>
        <MetricCard label="Last 7 days" value={recent} context="New customers" change={change?.text.split(" ")[0]} />
        <MetricCard label="Personal" value={r.last_7_days.people} context="People on their own" />
        <MetricCard label="Business" value={r.last_7_days.businesses} context="New business accounts" />
        <MetricCard
          label="Ordered already"
          value={r.last_7_days.ordered}
          suffix={`\u00a0of ${recent}`}
          context="Placed a first order"
        />
      </div>
      <div className={`${styles.panel} ${styles.regPanel}`}>
        <div className={styles.trend}>
          <LineChart
            label="New customers by day"
            categoryLabel="Day"
            curve="linear"
            height={180}
            data={chart.days.map((d) => ({
              key: d.date,
              label: d.label === "Today" ? `Today, ${chartDay(d.date)}` : chartDay(d.date),
              axisLabel: d.label,
              values: { recent: d.value, before: d.before },
            }))}
            series={[
              { key: "recent", label: "Last 7 days", area: true },
              { key: "before", label: "Same day a week earlier", dashed: true },
            ]}
            formatValue={(v) => String(Math.round(v))}
            formatTick={(v) => String(Math.round(v))}
          />
        </div>
        <div className={styles.newest}>
          <div className={styles.panelHead}>
            <h3 className={styles.title}>Newest</h3>
            <RouterLink href="/customers" className={styles.link}>
              All customers <ArrowRight size={14} aria-hidden />
            </RouterLink>
          </div>
          {r.newest.length === 0 ? (
            <p className={styles.nothing}>No one has registered yet.</p>
          ) : (
            <ul className={styles.rows}>
              {r.newest.map((n) => (
                <li key={n.id} className={styles.row}>
                  <span className={styles.avatar} data-kind={n.kind} aria-hidden>
                    {initials(n.name)}
                  </span>
                  <span className={styles.rowText}>
                    <RouterLink
                      href={`/${n.kind === "business" ? "businesses" : "customers"}/${n.id}`}
                      className={styles.rowTitle}
                    >
                      {n.name}
                    </RouterLink>
                    <span className={styles.hint}>
                      {n.kind === "business" ? "Business" : "Personal"} ·{" "}
                      {n.first_order ? `First order ${n.first_order}` : "No order yet"}
                    </span>
                  </span>
                  <span className={styles.hint}>{shortWhen(n.created_at, format.timezone)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}

/**
 * Overview (P8-24, redesigned in P8-56, `Console / Overview`): how the day is going and what needs
 * the signed-in person, only for what their role can see, each part opening the page behind it.
 */
export function OverviewPage({ item }: { item: ConsoleItem }): ReactNode {
  const { data: me } = useSuspenseQuery(meQuery);
  const format = useCompanyFormat();
  const navigate = useNavigate();
  const counts = useQuery(countsQuery());
  const figures = useQuery(overviewQuery);
  const permissions = me?.permissions ?? [];
  const name = me?.user.name.split(" ")[0] ?? "";
  const dispatch = counts.data?.dispatch;
  const header = (
    <PageHeader
      title={`${greeting(format.timezone)}${name ? `, ${name}` : ""}`}
      description={
        dispatch ? `${longDay(format.timezone)} · ${pipeline(dispatch).total} deliveries today` : item.description
      }
      actions={
        <>
          {dispatch ? (
            <Button variant="secondary" onClick={() => navigate({ to: "/fleet/map" })}>
              Open live map
            </Button>
          ) : null}
          {permissions.includes("orders.write") ? (
            <Button onClick={() => navigate({ to: "/orders/new" })}>New order</Button>
          ) : null}
        </>
      }
    />
  );

  if (counts.status === "pending" || figures.status === "pending")
    return (
      <>
        {header}
        <PageLoading label="Loading your day" />
      </>
    );
  if (counts.status === "error" || figures.status === "error") {
    const failed = counts.status === "error" ? counts : figures;
    return (
      <>
        {header}
        <PageError message={messageFor(errorOf(failed.error))} onRetry={() => failed.refetch()} />
      </>
    );
  }
  const c = counts.data;
  const f = figures.data;
  const seesWaiting = Boolean(c.dispatch || c.payouts || f.payments || f.tanks);
  const seesMoney = Boolean(f.payments || f.invoices);
  if (!seesWaiting && !seesMoney && !f.registrations)
    return (
      <>
        {header}
        <PageEmpty title="Nothing to count for your role" description="Use the menu to open a page." />
      </>
    );
  return (
    <>
      {header}
      <div className={styles.page}>
        {c.dispatch ? <Pipeline dispatch={c.dispatch} figures={f} /> : null}
        {seesWaiting || seesMoney ? (
          <div className={c.dispatch ? styles.columns : undefined}>
            <div className={styles.stack}>
              {seesWaiting ? <NeedsYou items={needsYou(c, f, format)} /> : null}
              <MoneyToday figures={f} />
            </div>
            {c.dispatch ? <FleetNow /> : null}
          </div>
        ) : null}
        {f.registrations ? <Registrations r={f.registrations} /> : null}
      </div>
    </>
  );
}
